import { useEffect, useRef } from 'react';

const GLYPHS = 'アイウエオカキクケコサシスセソ01<>[]{}#$%&*/\\PMR013';

/**
 * רקע "Matrix rain" אמביינטי — עדין ובעל ניגודיות נמוכה מכוון (ה-CSS מעמעם אותו),
 * כך שהוא לא מתחרה בקריאות הטקסט המהותי. מכובה לגמרי כש-animationsEnabled=false
 * או prefers-reduced-motion, ומושהה כשהטאב לא גלוי כדי לחסוך סוללה.
 */
export function MatrixRain({ enabled }: { enabled: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const fontSize = 16;
    let columns = 0;
    let drops: number[] = [];
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      columns = Math.ceil(window.innerWidth / fontSize);
      drops = new Array(columns).fill(0).map(() => Math.random() * -50);
    };
    resize();
    window.addEventListener('resize', resize);

    let raf = 0;
    let last = 0;
    let running = true;
    const FRAME_MS = 65; // ~15fps — מספיק לאווירה, זול על iPad

    const draw = (t: number) => {
      if (!running) return;
      raf = requestAnimationFrame(draw);
      if (t - last < FRAME_MS) return;
      last = t;

      ctx.fillStyle = 'rgba(1, 3, 2, 0.13)';
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);
      ctx.font = `${fontSize}px monospace`;

      for (let i = 0; i < columns; i++) {
        const glyph = GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
        const y = drops[i]! * fontSize;
        // shadowBlur יקר בקנבס — מוגבל רק לגלף הראש הבוהק, לא לכל תו בכל פריים (ביצועי iPad)
        const isHead = Math.random() > 0.92;
        if (isHead) {
          ctx.fillStyle = 'rgba(200, 255, 224, 0.98)';
          ctx.shadowColor = 'rgba(180, 255, 220, 0.8)';
          ctx.shadowBlur = 6;
        } else {
          ctx.fillStyle = 'rgba(0, 255, 102, 0.75)';
          ctx.shadowBlur = 0;
        }
        ctx.fillText(glyph!, i * fontSize, y);
        if (y > window.innerHeight && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]!++;
      }
    };
    raf = requestAnimationFrame(draw);

    const onVisibility = () => {
      running = !document.hidden;
      if (running) raf = requestAnimationFrame(draw);
      else cancelAnimationFrame(raf);
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled]);

  if (!enabled) return null;
  return <canvas ref={canvasRef} className="matrix-rain" aria-hidden="true" />;
}
